import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/fsl/Primitives";
import { ErrorState, LoadingState } from "@/components/fsl/States";
import { GroupsBlock } from "@/components/fsl/simple/GroupsBlock";
import { CalendarBlock } from "@/components/fsl/simple/CalendarBlock";
import { FinalsBlock } from "@/components/fsl/simple/FinalsBlock";
import { SimpleMatchEdit } from "@/components/fsl/SimpleMatchEdit";
import { SimpleMatchCreate } from "@/components/fsl/SimpleMatchCreate";
import { AiCalendarAssistant } from "@/components/fsl/AiCalendarAssistant";
import { FileDown, Sparkles } from "lucide-react";
import { ProgramPdfDialog } from "@/components/fsl/ProgramPdfDialog";
import { useTournamentDetail } from "@/hooks/useTournamentData";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function SimpleEngine() {
  const { tournamentId: tid } = useParams();
  const { data: t } = useTournamentDetail();
  const [params, setParams] = useSearchParams();
  const category = params.get("cat") || "";
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [editM, setEditM] = useState(null);
  const [createM, setCreateM] = useState(null);
  const [ai, setAi] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/tournaments/${tid}/simple/board`, { params: category ? { category } : {} }).then((r) => { setBoard(r.data); setError(null); }).catch(setError);
  }, [tid, category]);
  useEffect(load, [load]);

  const apply = (data) => { if (data.category === board?.category) setBoard(data); else load(); };
  const swap = async (a, b) => {
    try { const { data } = await api.post(`/tournaments/${tid}/simple/matches/swap`, { a, b }); apply(data); toast.success("Orario e campo scambiati"); } catch (e) { toast.error(apiError(e)); }
  };

  const undo = async (positions) => {
    try { const { data } = await api.post(`/tournaments/${tid}/simple/matches/restore`, { positions }); apply(data); toast.success(`Annullato: ${data.restored} ${data.restored === 1 ? "gara riportata" : "gare riportate"} al posto di prima`); } catch (e) { toast.error(apiError(e)); }
  };
  const undoable = (msg, positions) => toast.success(msg, { duration: 8000, action: positions?.length ? { label: "Annulla", onClick: () => undo(positions) } : undefined });
  const move = async (matchId, kickoff, fieldId, swap = true) => {
    try {
      const { data } = await api.post(`/tournaments/${tid}/simple/matches/move`, { match_id: matchId, kickoff_at: kickoff, field_id: fieldId, swap });
      apply(data);
      undoable(data.swapped ? "Partite scambiate" : "Partita spostata", data.undo);
      if (data.overlaps > 0 && window.confirm(`Dopo lo spostamento ${data.overlaps === 1 ? "una gara si accavalla" : `${data.overlaps} gare si accavallano`} su questo campo. Sposto anche le gare successive in avanti, a cascata?`)) await resolve(data.date, true);
    } catch (e) {
      if (e?.response?.status === 404) { load(); toast.error("Questa gara non è più nel calendario (è stato rigenerato o modificato altrove): ho ricaricato la griglia aggiornata, riprova da qui.", { duration: 7000 }); }
      else toast.error(apiError(e));
    }
  };

  const compact = async (day) => {
    if (!window.confirm(`Compattare la giornata del ${day.split("-").reverse().join("/")}? Le fasce di ${board?.category} vengono anticipate, intere e nello stesso ordine, nei primi orari liberi. Gare giocate e altre categorie non si toccano.`)) return;
    try { const { data } = await api.post(`/tournaments/${tid}/simple/matches/compact`, { date: day, category: board?.category }); apply(data); if (data.moved) undoable(`Giornata compattata: ${data.moved} ${data.moved === 1 ? "gara anticipata" : "gare anticipate"}`, data.undo); else toast.success("Nessun buco da chiudere: la giornata è già compatta"); } catch (e) { toast.error(apiError(e)); }
  };
  const moveRow = async (day, fromTime, toTime) => {
    if (!toTime || toTime === fromTime) return;
    try {
      const { data } = await api.post(`/tournaments/${tid}/simple/matches/move-row`, { date: day, from_time: fromTime, to_time: toTime, category: board?.category });
      apply(data);
      undoable(`Fascia ${fromTime} → ${toTime}: ${data.moved} ${data.moved === 1 ? "gara spostata" : "gare spostate"}`, data.undo);
      if (data.overlaps > 0 && window.confirm(`Dopo lo spostamento ${data.overlaps === 1 ? "una gara si accavalla" : `${data.overlaps} gare si accavallano`} su un campo. Sposto anche le gare successive in avanti, a cascata?`)) await resolve(data.date, true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (day, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm(`Risolvere le sovrapposizioni del ${day.split("-").reverse().join("/")}? Le gare che si accavallano sullo stesso campo vengono spostate in avanti, in cascata (anche di altre categorie). Le gare già giocate non si toccano.`)) return;
    try { const { data } = await api.post(`/tournaments/${tid}/simple/matches/resolve-overlaps`, { date: day, category: board?.category }); apply(data); if (data.moved) undoable(`${data.moved} gare spostate in avanti`, data.undo); else toast.success("Nessuna sovrapposizione sui campi (restano eventuali conflitti di squadra)"); } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = async (m) => {
    if (!m.category || m.category === board?.category) return setEditM(m);
    try { const { data } = await api.get(`/tournaments/${tid}/simple/board`, { params: { category: m.category } }); setEditM({ ...m, _teams: data.teams }); } catch (e) { toast.error(apiError(e)); }
  };

  const saveSlots = async (date, add, remove, onBusy = "refuse") => {
    try {
      const { data } = await api.put(`/tournaments/${tid}/simple/slots`, { category: board?.category, date, add, remove, on_busy: onBusy });
      setBoard(data);
      if (data.moved) undoable(`Fascia eliminata: ${data.moved} ${data.moved === 1 ? "partita spostata" : "partite spostate"} nella prima fascia libera successiva`, data.undo);
      else if (data.deleted) toast.success(`Fascia eliminata: ${data.deleted} ${data.deleted === 1 ? "partita cancellata" : "partite cancellate"} (recuperabili dal cestino del calendario)`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "SLOT_BUSY") {
        const n = d.count;
        if (window.confirm(`La fascia ${d.time} ha ${n} ${n === 1 ? "partita" : "partite"} di ${d.category}.\n\nOK = sposto ${n === 1 ? "la partita" : "le partite"} nella prima fascia libera successiva (stesso campo)\nAnnulla = scelgo se cancellarle`)) return saveSlots(date, add, remove, "move");
        if (window.confirm(`Cancellare ${n === 1 ? "la partita" : `le ${n} partite`} delle ${d.time} insieme alla fascia? Restano recuperabili dal cestino del calendario.`)) return saveSlots(date, add, remove, "delete");
        return;
      }
      toast.error(apiError(e));
    }
  };
  const saveBreaks = async (breaks) => {
    try { const { data } = await api.put(`/tournaments/${tid}/simple/breaks`, { category: board?.category, breaks }); setBoard(data); toast.success("Pause aggiornate"); } catch (e) { toast.error(apiError(e)); }
  };

  const quickTeam = async (matchId, side, teamId, force = false) => {
    try { await api.patch(`/tournaments/${tid}/groups/matches/${matchId}`, { [`${side}_team_id`]: teamId, force }); toast.success("Squadra cambiata"); load(); } catch (e) {
      if (e?.response?.status === 409 && !force && window.confirm(`${apiError(e)}\n\nCambiare comunque?`)) return quickTeam(matchId, side, teamId, true);
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={apiError(error)} onRetry={load} />;
  if (!board || !t) return <LoadingState label="Caricamento gironi…" />;
  const canWrite = ["super_admin", "director", "secretary"].includes(t.my_role) && !t.read_only;
  const total = board.matches.length + board.finals.length;

  return (
    <div className="space-y-6" data-testid="simple-engine">
      <PageHeader
        kicker="Una pagina, tre passi"
        title="Gironi e Calendario"
        subtitle={`${board.groups.length} gironi · ${board.teams.filter((x) => board.groups.some((g) => g.id === x.competition_id)).length} squadre · ${total} partite`}
        actions={<div className="flex flex-wrap items-center gap-2">
          {canWrite && <button type="button" className={`h-10 px-4 inline-flex items-center gap-2 rounded-md font-semibold text-sm border transition-colors ${ai ? "bg-fsl-gold text-ink-950 border-fsl-gold" : "border-fsl-gold/60 text-fsl-gold hover:bg-fsl-gold/10"}`} onClick={() => setAi((v) => !v)} data-testid="ai-toggle"><Sparkles className="h-4 w-4" /> Assistente IA</button>}
          <button type="button" className="btn-gold h-10" onClick={() => setPdfOpen(true)} data-testid="program-pdf-button"><FileDown className="h-4 w-4" /> PDF Premium</button>
          {board.categories.length > 1 && (
            <select className="fsl-input h-10 w-44" value={board.category} onChange={(e) => { const p = new URLSearchParams(params); p.set("cat", e.target.value); setParams(p); }} data-testid="simple-category-select">
              {board.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>}
      />
      <GroupsBlock tid={tid} board={board} reload={load} canWrite={canWrite} />
      <CalendarBlock tid={tid} board={board} reload={load} canWrite={canWrite} onEdit={openEdit} onResolve={resolve} onCompact={compact} onMoveRow={moveRow} onSwap={swap} onMove={move} onBreaks={saveBreaks} onSlots={saveSlots} onQuickTeam={quickTeam} onCreate={setCreateM} onCategory={(cat) => { const p = new URLSearchParams(params); p.set("cat", cat); setParams(p); }} />
      <FinalsBlock tid={tid} board={board} reload={load} canWrite={canWrite} onEdit={openEdit} onSwap={swap} onCreate={setCreateM} />
      {ai && <AiCalendarAssistant tid={tid} board={board} onApplied={(data) => setBoard(data)} onClose={() => setAi(false)} />}
      {createM && <SimpleMatchCreate tid={tid} board={board} defaults={createM} onClose={() => setCreateM(null)} onDone={load} />}
      <ProgramPdfDialog open={pdfOpen} onOpenChange={setPdfOpen} tid={tid} categories={board.categories.length ? board.categories : [board.category]} category={board.category} />
      {editM && <SimpleMatchEdit tid={tid} m={editM} teams={editM._teams || board.teams} fields={board.fields} onClose={() => setEditM(null)} onDone={load} />}
    </div>
  );
}
